import React, { useState, useRef, useEffect } from 'react';
import '../styles/PinAuthentication.css';

const PinAuthentication = ({ isOpen, onClose, onSuccess }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const inputRef = useRef(null);

  const CORRECT_PIN = '2002';
  const MAX_ATTEMPTS = 3;

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleNumberClick = (number) => {
    if (pin.length < 4) {
      const newPin = pin + number;
      setPin(newPin);
      setError(''); // Clear any previous errors
      
      // Auto-verify when 4 digits are entered
      if (newPin.length === 4) {
        setTimeout(() => {
          verifyPin(newPin);
        }, 300); // Small delay for better UX
      }
    }
  };

  const handleBackspace = () => {
    if (pin.length > 0) {
      setPin(pin.slice(0, -1));
      setError(''); // Clear error when editing
    }
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const verifyPin = (pinValue) => {
    if (pinValue === CORRECT_PIN) {
      // Correct PIN - show modal
      onSuccess();
      setPin('');
      setError('');
      setAttempts(0);
      onClose();
    } else {
      // Incorrect PIN - clear input and show error
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= MAX_ATTEMPTS) {
        setError('Accès refusé. Trop de tentatives incorrectes.');
        setTimeout(() => {
          onClose();
          setPin('');
          setError('');
          setAttempts(0);
        }, 2000);
      } else {
        setError(`Code PIN incorrect. ${MAX_ATTEMPTS - newAttempts} tentative(s) restante(s).`);
        // Auto-clear the PIN for immediate retry
        setTimeout(() => {
          setPin('');
        }, 1000); // Clear after 1 second to show error message
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pin-modal-overlay">
      <div className="pin-modal-content">
        <div className="pin-header">
          <h3>
            <i className="fas fa-lock"></i>
            Authentification requise
          </h3>
          <button className="pin-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="pin-body">
          <div className="pin-icon">
            <i className="fas fa-lock"></i>
          </div>
          
          <div className="pin-title">
            <h4>Accès Sécurisé</h4>
            <p>Entrez le code PIN pour accéder au tableau de bord</p>
          </div>

          <div className="pin-input-section">
            <div className="pin-display">
              <div className="pin-dots">
                {[1, 2, 3, 4].map((dot) => (
                  <div 
                    key={dot} 
                    className={`pin-dot ${pin.length >= dot ? 'filled' : ''}`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="pin-error">
                <i className="fas fa-exclamation-triangle"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="pin-keypad">
              <div className="keypad-row">
                <button className="keypad-btn" onClick={() => handleNumberClick('1')}>1</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('2')}>2</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('3')}>3</button>
              </div>
              <div className="keypad-row">
                <button className="keypad-btn" onClick={() => handleNumberClick('4')}>4</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('5')}>5</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('6')}>6</button>
              </div>
              <div className="keypad-row">
                <button className="keypad-btn" onClick={() => handleNumberClick('7')}>7</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('8')}>8</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('9')}>9</button>
              </div>
              <div className="keypad-row">
                <button className="keypad-btn clear-btn" onClick={handleClear}>C</button>
                <button className="keypad-btn" onClick={() => handleNumberClick('0')}>0</button>
                <button className="keypad-btn backspace-btn" onClick={handleBackspace}>
                  <i className="fas fa-backspace"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="pin-status">
            <div className="pin-attempts">
              <i className="fas fa-shield-alt"></i>
              <span>Tentatives: {attempts}/{MAX_ATTEMPTS}</span>
            </div>
            <div className="pin-requirement">
              <i className="fas fa-key"></i>
              <span>4 chiffres requis</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PinAuthentication;

